// @avantia common-code
import { ensureDefined, firstDefined, SMap } from '@avantia/client-and-server-utilities';
import {
  createControlId,
  findQuestionId,
  HpAddressItemResponse,
  HpAddressLookupActionProps,
  HpAnswerValueTypes,
  HpQuestionData,
  HpQuestionLookupItem,
  HpStandardControlTypes
} from '@avantia/questionset-model';
import { HpDynamicFunctionProps, HpPrerequisiteThisReference } from '@avantia/questionset-script-parser';
import { HpClientDebugFlags } from '../clientDebugFlags';
import {
  HpAddressItemAction,
  HpAddressItemFailedAction,
  HpAddressItemPayload,
  HpAddressLookupAction,
  HpAddressLookupButtonClickActionProps,
  HpAddressLookupFailedAction,
  HpAddressLookupPayload,
  HpClientQuestionSetState,
  HpEngineActionTypes
} from '../types';
import { processPrereqsAndCalcs, removeUiProperties, updateAnswer, updateQuestion } from './questionSetReducerLibrary';
import { addErrorToValidation, createBlankAnswer } from './questionSetTools';
import { applyChanges, applyInnerChanges } from './reducerLibrary';
import { ReducerProcessor } from './reducerProcessor';

export function registerAddressLookupFeature(
  processor: ReducerProcessor<HpClientQuestionSetState, HpEngineActionTypes>
): void {
  processor.add<HpAddressLookupAction>('ADDRESS_LOOKUP_CALL_STARTED', (state, { payload }) => {
    const { lookupResultsId } = payload as HpAddressLookupPayload;
    return clearAddressLookupResult(lookupResultsId, state);
  });

  processor.add<HpAddressLookupAction>('ADDRESS_LOOKUP_RETRIEVED', (state, { payload }) => {
    const {
      addresses,
      noResultsFoundMessage,
      lookupResultsId,
      lookupButtonId,
      searchFields: searchFieldsIds,
      groupNumber
    } = payload as HpAddressLookupPayload;
    ensureDefined(lookupResultsId, 'lookupResultsId');
    ensureDefined(lookupButtonId, 'lookupButtonId');

    const questionId = findQuestionId({ name: lookupResultsId, questions: state.questions, groupNumber });
    const foundAddresses = addresses.length > 0;

    state = applyInnerChanges(
      state,
      [
        'validationResult',
        'errors',
        groupNumber
          ? createControlId({ questionId: lookupButtonId, groupNumber, options: { noPrefix: true } })
          : lookupButtonId
      ],
      !foundAddresses ? noResultsFoundMessage || 'Your address search returned no results' : '',
      { copyRootNode: true }
    );

    let searchFields: string[];
    if (groupNumber) {
      searchFields = searchFieldsIds.map((f) =>
        createControlId({ questionId: f, groupNumber, options: { noPrefix: true } })
      );
    } else {
      searchFields = searchFieldsIds;
    }

    state = removeUiProperties(state, searchFields, ['isDirty', 'isTouched']);

    state = updateQuestion(questionId, state, (question) => {
      const lookup: SMap<HpQuestionLookupItem> = {};
      addresses.forEach(({ id, address }) => {
        lookup[id] = { text: address };
      });

      applyInnerChanges(question, ['data', 'lookup'], lookup);
      return question;
    });

    const partialAddress = createBlankAddress();
    let answers = state.answers;
    searchFields.forEach((f) => {
      if (groupNumber) {
        f = createControlId({ questionId: f, groupNumber, options: { noPrefix: true } });
      }

      if (!answers[f]) {
        if (answers === state.answers) {
          answers = { ...answers };
        }

        answers[f] = createBlankAnswer(HpClientDebugFlags.AddressLookup);
      }

      partialAddress[f] = answers[f].customer;
    });

    ({ state } = updateAnswer({
      currState: state,
      eventType: 'server-action',
      questionId,
      value: null,
      partialAnswer: { partial: partialAddress }
    }));

    return processPrereqsAndCalcs('click', state);
  });

  processor.add<HpAddressItemAction | HpAddressLookupAction>('ADDRESS_ITEM_RETRIEVED', (state, { payload }) => {
    const address = firstDefined<HpAddressItemResponse>(
      (payload as HpAddressLookupPayload).address,
      payload as HpAddressItemPayload
    );

    if (!address || !address.postcode) {
      throw new Error('The address value has not been provided.');
    }

    const {
      groupNumber,
      lookupResultsId: lookupResultsQid,
      searchFields: searchFieldsIds
    } = payload as HpAddressLookupPayload;
    let lookupResultsId: string;
    if (groupNumber) {
      lookupResultsId = createControlId({ questionId: lookupResultsQid, groupNumber, options: { noPrefix: true } });
    } else {
      lookupResultsId = lookupResultsQid;
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    state = applyChanges(state, {
      validationResult: addErrorToValidation(state.validationResult, lookupResultsId, undefined)
    });
    const questionId = findQuestionId({ name: lookupResultsId, questions: state.questions });
    state = clearAddressLookupResult(questionId, state);

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    let searchFields: string[];
    if (groupNumber) {
      searchFields = searchFieldsIds.map((f) =>
        createControlId({ questionId: f, groupNumber, options: { noPrefix: true } })
      );
    } else {
      searchFields = searchFieldsIds;
    }

    state = removeUiProperties(state, searchFields, ['isDirty', 'isTouched']);

    ({ state } = updateAnswer({
      currState: state,
      eventType: 'server-action',
      questionId,
      value: address.addressId,
      partialAnswer: { partial: address }
    }));

    state.displayState = applyChanges(state.displayState, { incrementedSectionId: undefined });
    return processPrereqsAndCalcs('click', state);
  });

  processor.add<HpAddressLookupFailedAction>('ADDRESS_LOOKUP_FAILED', (state, { payload }) => {
    const {
      errorLoadingAddressesMessage,
      lookupButtonId: lookupButtonQid,
      groupNumber
    } = ensureDefined(payload, 'payload');

    const lookupButtonId = groupNumber
      ? createControlId({ questionId: lookupButtonQid, groupNumber, options: { noPrefix: true } })
      : lookupButtonQid;
    const validationResult = addErrorToValidation(state.validationResult, lookupButtonId, errorLoadingAddressesMessage);
    return applyChanges(state, { validationResult });
  });

  processor.add<HpAddressItemFailedAction>('ADDRESS_ITEM_FAILED', (state, { payload }) => {
    const {
      errorLoadingAddressMessage,
      lookupResultsId: lookupResultsQid,
      groupNumber
    } = ensureDefined(payload, 'payload');

    const lookupResultsId = groupNumber
      ? createControlId({ questionId: lookupResultsQid, groupNumber, options: { noPrefix: true } })
      : lookupResultsQid;

    const validationResult = addErrorToValidation(state.validationResult, lookupResultsId, errorLoadingAddressMessage);
    return applyChanges(state, { validationResult });
  });

  function clearAddressLookupResult(
    lookupResultsId: string,
    state: HpClientQuestionSetState
  ): HpClientQuestionSetState {
    return updateQuestion(lookupResultsId, state, (question) => {
      const lookup: SMap<HpQuestionLookupItem> = {};
      question = applyInnerChanges(question, ['data', 'lookup'], lookup);
      return question;
    });
  }
}

export function addressLookupButtonClickAction({
  currState,
  props,
  question
}: HpAddressLookupButtonClickActionProps): HpClientQuestionSetState {
  const { searchFields } = (props as HpAddressLookupActionProps) || { searchFields: [] };
  const searchField = searchFields[0];
  const answer = currState.answers[searchField];
  if (answer === undefined) {
    throw new Error(
      `The 'searchField' must ${searchField ? 'be provided' : 'be the ID of a question'} in the actionProps for "${
        question.questionId
      }".`
    );
  }

  return currState;
}

export function getAddressDetailAnswer(
  this: HpPrerequisiteThisReference,
  { questionId }: HpDynamicFunctionProps
): HpAnswerValueTypes {
  const answers: string[] = [];
  const prefixKey = 'addressControlPrefix';
  const question = this.getQuestion(questionId);
  const data = question.data as HpQuestionData;
  const { lookup, textAndStyle } = data;
  const expectedControlType = 'AddressView';
  const controlType = textAndStyle.standard.controlType;
  if (controlType !== (expectedControlType as HpStandardControlTypes)) {
    throw new Error(
      `The question "${questionId}" is requesting address details but is not of the correct controlType. Expected "${expectedControlType}", received: "${controlType}".`
    );
  }

  const { text: addressControlPrefix } = (lookup ? lookup[prefixKey] : null) || {};
  if (addressControlPrefix === undefined) {
    throw new Error(
      `The lookup for question "${questionId}" ('${
        question.name || questionId
      }') must contain a '${prefixKey}' key which contains property 'text' which contains the address prefix required (e.g. 'billing', 'contact').`
    );
  }

  const addPrefix = (name: string): string => {
    if (!addressControlPrefix) {
      return name;
    }

    return addressControlPrefix + name.substring(0, 1).toUpperCase() + name.substring(1);
  };

  const hasAddressLine1 = this.getAnswerValue(addPrefix('addressLine1'));
  const questionIds = [
    'propertyNameNumber',
    'addressLine1',
    'addressLine2',
    'townCity',
    'county',
    'postcode',
    'addressLookupJson'
  ];
  questionIds.forEach((name) => {
    const fullName = addPrefix(name);
    answers.push(name);
    answers.push(hasAddressLine1 ? this.getAnswerValue(fullName) : '');
  });
  return answers;
}

function createBlankAddress(): HpAddressItemResponse {
  return {
    addressId: '',
    addressLine1: '',
    addressLine2: '',
    county: '',
    postcode: '',
    propertyNameNumber: '',
    success: true,
    townCity: '',
    addressLookupJson: '',
    type: 'address',
    timeTaken: ''
  };
}
